// pages/UserAdmin/crm/ImportCsvDialog.js
//
// Importação de contatos por planilha, em dois passos: primeiro a pessoa
// confere o que vai acontecer, depois confirma.
//
// ─── Por que conferir antes ────────────────────────────────────────────
// Importação é escrita em massa e não tem "desfazer". A tela mostra, antes
// de gravar qualquer coisa, quantos contatos nascem, quantos são
// atualizados, quantas linhas estão sem telefone, e para onde vai cada
// coluna da planilha. Mesmo padrão do disparo em lote.
//
// ─── Por que o arquivo sobe duas vezes ─────────────────────────────────
// Uma no passo de conferência, outra na confirmação. Guardar o arquivo no
// servidor entre os dois passos exigiria estado no processo, e com
// `--workers 1` um restart perderia o arquivo no meio do fluxo. Planilha
// de contatos é pequena; subir de novo custa menos que a fragilidade.
//
// ─── O que a tela NÃO deixa acontecer ──────────────────────────────────
// Coluna desconhecida nasce em "Não importar": nada entra no CRM por
// omissão. Criar campo personalizado é escolha explícita por coluna, e a
// tela avisa quantas vagas restam das 30 da empresa.

import { useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import api from '../../../services/Api';
import { Icon } from './icons';
import { mensagemDeErro } from './erros';

const SYNE = "'Syne', system-ui, sans-serif";
// Verde da marca pelo tema (claro/escuro).
const GREEN = 'var(--ch-green)';

const IGNORAR = 'IGNORAR';
const TELEFONE = 'TELEFONE';
const NOVO_CAMPO = 'NOVO_CAMPO';

// O que o seletor de arquivo do sistema oferece. .xlsx entra em out/26:
// quem exporta do Excel ou do Google Sheets salva .xlsx por padrão, e o
// filtro só com .csv fazia a planilha da pessoa aparecer apagada na
// janela — ela concluía que não dava pra importar.
//
// É só conveniência: quem decide o formato é o backend, que detecta pelos
// BYTES (zip começa com "PK") e não pela extensão. CSV renomeado pra
// .xlsx continua funcionando.
const ACEITOS = [
  '.csv', '.tsv', '.xlsx',
  'text/csv', 'text/tab-separated-values',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

/** Quadradinho de número do resumo. */
function Placar({ valor, rotulo, cor, bg }) {
  return (
    <Box sx={{ bgcolor: bg, borderRadius: 2, px: 1.75, py: 1, minWidth: 92 }}>
      <Typography sx={{ fontFamily: SYNE, fontWeight: 800, fontSize: '1.3rem', color: cor }}>
        {valor}
      </Typography>
      <Typography variant="caption" sx={{ color: cor, opacity: 0.85 }}>
        {rotulo}
      </Typography>
    </Box>
  );
}

export default function ImportCsvDialog({ open, onClose, onImported }) {
  const token = localStorage.getItem('token');
  // `Content-Type` explícito: o axios do repo força application/json em
  // toda requisição (services/Api.js), e com FormData isso manda o corpo
  // SEM o boundary do multipart — o backend recebe "file: Field required".
  // Mesmo contorno que os outros uploads do app já usam.
  const auth = {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  };
  const inputRef = useRef(null);

  const [arquivo, setArquivo] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [destinos, setDestinos] = useState({});   // {indice: destino}
  const [lendo, setLendo] = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');

  /** Pede a conferência ao servidor. `colunaTelefone` só vai quando a
   *  pessoa TROCA a coluna: o placar (novos / já existem / sem telefone) é
   *  calculado em cima dela, e sem reconsultar ele continuaria mostrando os
   *  números da coluna que o palpite escolheu — prometendo "0 novos" numa
   *  importação que vai criar centenas. */
  const conferir = async (f, colunaTelefone) => {
    setErro('');
    setLendo(true);
    try {
      const form = new FormData();
      form.append('file', f);
      if (colunaTelefone !== undefined && colunaTelefone !== null) {
        form.append('phone_column', String(colunaTelefone));
      }
      const res = await api.post('/contacts/import/preview', form, auth);
      setPrevia(res.data);
      return res.data;
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível ler a planilha.'));
      return null;
    } finally {
      setLendo(false);
    }
  };

  const escolher = async (e) => {
    const f = e.target.files?.[0];
    // Limpa o input pra escolher o MESMO arquivo de novo disparar o evento
    // (depois de corrigir a planilha e salvar por cima, que é o normal).
    e.target.value = '';
    if (!f) return;
    setArquivo(f);
    setPrevia(null);
    setResultado(null);
    const dados = await conferir(f);
    if (dados) {
      setDestinos(Object.fromEntries(
        dados.colunas.map((c) => [c.indice, c.destino_sugerido])
      ));
    } else {
      setArquivo(null);
    }
  };

  const trocarDestino = (indice, valor) => {
    setDestinos((prev) => {
      const next = { ...prev, [indice]: valor };
      // Destino de coluna única não pode ficar em duas colunas: a segunda
      // sobrescreveria a primeira em silêncio durante a importação.
      if (valor !== IGNORAR && valor !== NOVO_CAMPO) {
        Object.keys(next).forEach((k) => {
          if (Number(k) !== indice && next[k] === valor) next[k] = IGNORAR;
        });
      }
      return next;
    });
    // O placar depende de QUAL coluna é o telefone. Trocou, reconta.
    if (valor === TELEFONE && arquivo) conferir(arquivo, indice);
  };

  const lista = Object.values(destinos);
  const temTelefone = lista.includes(TELEFONE);
  const novosCampos = lista.filter((d) => d === NOVO_CAMPO).length;
  const semVaga = previa ? novosCampos > previa.vagas_para_campos : false;

  const importar = async () => {
    if (!arquivo || !temTelefone || semVaga) return;
    setImportando(true);
    setErro('');
    try {
      const form = new FormData();
      form.append('file', arquivo);
      form.append('mapping', JSON.stringify(destinos));
      const res = await api.post('/contacts/import', form, auth);
      setResultado(res.data);
      onImported?.(res.data);
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível importar.'));
    } finally {
      setImportando(false);
    }
  };

  const fechar = () => {
    if (importando) return;
    setArquivo(null);
    setPrevia(null);
    setDestinos({});
    setResultado(null);
    setErro('');
    onClose?.();
  };

  return (
    <Dialog
      open={open} onClose={fechar} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, color: 'var(--ch-text)', pb: 1 }}>
        Importar contatos
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--cx-cap)', fontWeight: 500 }}>
          {resultado ? 'Resultado' : arquivo?.name || 'Planilha do Excel, Google Sheets ou outro CRM (.xlsx ou .csv)'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {erro && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{erro}</Alert>}

        {/* ─── 3. Resultado ─── */}
        {resultado ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Placar valor={resultado.criados} rotulo="criados" cor="var(--ch-green-d)" bg="var(--ch-green-bg)" />
              <Placar valor={resultado.atualizados} rotulo="atualizados" cor="var(--ch-blue-d)" bg="var(--ch-blue-bg)" />
              {resultado.total_erros > 0 && (
                <Placar valor={resultado.total_erros} rotulo="com problema" cor="var(--ch-amber-d)" bg="var(--ch-amber-bg)" />
              )}
            </Stack>

            {resultado.campos_criados?.length > 0 && (
              <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                Campos personalizados criados:{' '}
                {resultado.campos_criados.map((c) => c.label).join(', ')}.
              </Alert>
            )}

            {resultado.erros?.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: 'var(--ch-red-d)', fontWeight: 700 }}>
                  Linhas que NÃO entraram
                  {resultado.total_erros > resultado.erros.length
                    && ` (mostrando ${resultado.erros.length} de ${resultado.total_erros})`}
                </Typography>
                <Box sx={{ maxHeight: 160, overflow: 'auto', mt: 0.5 }}>
                  {resultado.erros.map((e, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'var(--cx-cap)' }}>
                      <strong>Linha {e.linha}</strong> — {e.motivo}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Separado dos erros de propósito: estas linhas ENTRARAM, só
                alguma célula foi descartada. Listá-las como "não entraram"
                assustaria à toa. */}
            {resultado.alertas?.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: 'var(--ch-amber-d)', fontWeight: 700 }}>
                  Entraram, com uma ressalva
                  {resultado.total_alertas > resultado.alertas.length
                    && ` (mostrando ${resultado.alertas.length} de ${resultado.total_alertas})`}
                </Typography>
                <Box sx={{ maxHeight: 160, overflow: 'auto', mt: 0.5 }}>
                  {resultado.alertas.map((a, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'var(--cx-cap)' }}>
                      <strong>Linha {a.linha}</strong> — {a.motivo}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        ) : !previa ? (
          /* ─── 1. Escolher o arquivo ─── */
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <input
              ref={inputRef}
              type="file"
              // .xlsx entra em out/26. O backend detecta o formato pelos BYTES
              // (zip começa com PK), não pela extensão — este accept é só
              // pra conveniência do seletor do sistema.
              accept={ACEITOS}
              onChange={escolher}
              style={{ display: 'none' }}
            />
            {lendo ? (
              <>
                <CircularProgress size={26} sx={{ color: GREEN }} />
                <Typography variant="caption" sx={{ color: 'var(--cx-cap)' }}>
                  Lendo a planilha…
                </Typography>
              </>
            ) : (
              <>
                <Button
                  onClick={() => inputRef.current?.click()}
                  variant="outlined"
                  startIcon={<Icon.Upload size={15} />}
                  sx={{
                    textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                    borderColor: 'var(--ch-bdr2)', color: 'var(--ch-text)', borderRadius: 2, px: 3,
                  }}
                >
                  Escolher planilha
                </Button>
                <Typography variant="caption" sx={{ color: 'var(--cx-cap)', textAlign: 'center' }}>
                  Precisa ter uma coluna com o telefone. As outras você escolhe
                  no passo seguinte — nada entra sem você confirmar.
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--ch-text3)', textAlign: 'center' }}>
                  Dica: exporte seus contatos primeiro, complete no Excel e
                  importe de volta.
                </Typography>
              </>
            )}
          </Stack>
        ) : (
          /* ─── 2. Conferência ─── */
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {(previa.avisos || []).map((a, i) => (
              <Alert key={i} severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                {a}
              </Alert>
            ))}

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Placar valor={previa.resumo.novos} rotulo="novos" cor="var(--ch-green-d)" bg="var(--ch-green-bg)" />
              <Placar valor={previa.resumo.atualizados} rotulo="já existem" cor="var(--ch-blue-d)" bg="var(--ch-blue-bg)" />
              {previa.resumo.sem_telefone > 0 && (
                <Placar valor={previa.resumo.sem_telefone} rotulo="sem telefone" cor="var(--ch-amber-d)" bg="var(--ch-amber-bg)" />
              )}
              {previa.resumo.repetidos_no_arquivo > 0 && (
                <Placar
                  valor={previa.resumo.repetidos_no_arquivo}
                  rotulo="repetidos no arquivo" cor="var(--ch-amber-d)" bg="var(--ch-amber-bg)"
                />
              )}
            </Stack>

            <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
              Quem já está na base é <strong>atualizado</strong>: coluna em
              branco não apaga o que está no CRM.
            </Alert>

            <Divider />

            <Box>
              <Typography variant="caption" sx={{ color: 'var(--ch-text2)', fontWeight: 700, display: 'block', mb: 0.25 }}>
                Para onde vai cada coluna
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--cx-cap)', display: 'block', mb: 1.5 }}>
                {previa.total_linhas} linha{previa.total_linhas === 1 ? '' : 's'} na
                planilha. Confira os palpites antes de importar.
              </Typography>

              <Stack spacing={1.75}>
                {previa.colunas.map((col) => (
                  <Box
                    key={col.indice}
                    sx={{ border: '1px solid var(--ch-bdr)', borderRadius: 2, p: 1.5 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={col.cabecalho}
                        sx={{ fontWeight: 700, bgcolor: 'var(--ch-inset)', color: 'var(--ch-text)', maxWidth: 240 }}
                      />
                      {col.amostra.length > 0 && (
                        <Typography variant="caption" sx={{ color: 'var(--cx-cap)', minWidth: 0 }}>
                          ex.: {col.amostra.slice(0, 2).join(' · ')}
                        </Typography>
                      )}
                    </Stack>
                    <TextField
                      select fullWidth size="small"
                      label="Importar como"
                      value={destinos[col.indice] ?? IGNORAR}
                      onChange={(e) => trocarDestino(col.indice, e.target.value)}
                      disabled={importando}
                    >
                      {previa.destinos.map((d) => (
                        <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                ))}
              </Stack>
            </Box>

            {!temTelefone && (
              <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                Escolha qual coluna tem o <strong>telefone</strong>. Sem ele não
                dá pra saber quem é quem nem pra enviar mensagem depois.
              </Alert>
            )}
            {novosCampos > 0 && !semVaga && (
              <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                {novosCampos} campo{novosCampos === 1 ? '' : 's'} personalizado
                {novosCampos === 1 ? ' será criado' : 's serão criados'} na
                importação. Restam {previa.vagas_para_campos} vaga
                {previa.vagas_para_campos === 1 ? '' : 's'}.
              </Alert>
            )}
            {semVaga && (
              <Alert severity="error" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                Você marcou {novosCampos} campos novos, mas só restam{' '}
                {previa.vagas_para_campos}. Deixe algumas colunas em "Não
                importar" ou apague um campo que não use mais.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid var(--ch-bdr)' }}>
        <Button
          onClick={fechar} disabled={importando}
          sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: 'var(--cx-cap)' }}
        >
          {resultado ? 'Fechar' : 'Cancelar'}
        </Button>
        {previa && !resultado && (
          <Button
            onClick={importar}
            disabled={importando || !temTelefone || semVaga}
            variant="contained" disableElevation
            startIcon={importando
              ? <CircularProgress size={14} color="inherit" />
              : <Icon.Upload size={14} />}
            sx={{
              textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
            }}
          >
            {/* Conta o que REALMENTE vira contato — não as linhas do
                arquivo. Com `total_linhas` o botão dizia "Importar 4"
                logo abaixo de um resumo que dizia "2 novos · 1 já existe
                · 1 sem telefone", ou seja, prometia um número que ele
                mesmo acabara de desmentir. */}
            {importando
              ? 'Importando…'
              : `Importar ${previa.resumo.novos + previa.resumo.atualizados}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
